/**
 * Profile360 Query Router
 * Intelligent routing system that maps natural language queries to API endpoints
 */

import queryMappings from '../config/queryMappings.json';
import apiService from './apiService';

/**
 * Query Router class for Profile360
 */
class QueryRouter {
  constructor() {
    this.mappings = queryMappings;
    this.apiService = apiService;
  }

  /**
   * Extract parameters from query text using pattern matching
   * @param {string} query - User query
   * @param {string} pattern - Pattern to match against
   * @returns {Object} Extracted parameters
   */
  extractParameters(query, pattern) {
    console.log('🚀 EXTRACT PARAMETERS CALLED!');
    console.log('📝 Input Query:', query);
    console.log('🎯 Input Pattern:', pattern);

    const params = {};
    const queryLower = query.toLowerCase();
    const patternLower = pattern.toLowerCase();

    console.log('📝 Query Lower:', queryLower);
    console.log('🎯 Pattern Lower:', patternLower);
    
    // Find parameter placeholders in pattern (e.g., {user_id}, {user_email})
    const paramRegex = /\{([^}]+)\}/g;
    let match;
    
    while ((match = paramRegex.exec(patternLower)) !== null) {
      const paramName = match[1];
      const paramPlaceholder = match[0];
      
      // Create regex to extract the actual value from query
      const beforeParam = patternLower.substring(0, match.index);
      const afterParam = patternLower.substring(match.index + paramPlaceholder.length);
      
      // Build extraction regex
      let extractRegex;
      if (beforeParam && afterParam) {
        // For user names, capture multiple words until the next pattern part
        if (paramName === 'user_name') {
          extractRegex = new RegExp(
            `${this.escapeRegex(beforeParam)}\\s*(.+?)\\s*${this.escapeRegex(afterParam)}`,
            'i'
          );
        } else {
          extractRegex = new RegExp(
            `${this.escapeRegex(beforeParam)}\\s*([^\\s]+)\\s*${this.escapeRegex(afterParam)}`,
            'i'
          );
        }
      } else if (beforeParam) {
        // For user names at the end, capture everything after the pattern
        if (paramName === 'user_name') {
          extractRegex = new RegExp(`${this.escapeRegex(beforeParam)}\\s*(.+)$`, 'i');
        } else {
          extractRegex = new RegExp(`${this.escapeRegex(beforeParam)}\\s*([^\\s]+)`, 'i');
        }
      } else if (afterParam) {
        extractRegex = new RegExp(`([^\\s]+)\\s*${this.escapeRegex(afterParam)}`, 'i');
      } else {
        // Pattern is just the parameter
        extractRegex = new RegExp('(.+)', 'i');
      }
      
      const extractMatch = queryLower.match(extractRegex);
      if (extractMatch) {
        params[paramName] = extractMatch[1].trim();

        // Special logging for user_email parameter extraction
        if (paramName === 'user_email') {
          console.log('📧 EMAIL EXTRACTION DEBUG:');
          console.log('🔍 Pattern:', pattern);
          console.log('📝 Query:', queryLower);
          console.log('🎯 Regex:', extractRegex);
          console.log('✅ Extracted email:', extractMatch[1].trim());
        }

        // Special logging for user_name parameter extraction
        if (paramName === 'user_name') {
          console.log('👤 USER NAME EXTRACTION DEBUG:');
          console.log('🔍 Pattern:', pattern);
          console.log('📝 Query:', queryLower);
          console.log('🎯 Regex:', extractRegex);
          console.log('✅ Extracted user_name:', extractMatch[1].trim());
        }
      }
    }
    
    return params;
  }

  /**
   * Escape special regex characters
   * @param {string} string - String to escape
   * @returns {string} Escaped string
   */
  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Calculate similarity score between query and pattern
   * @param {string} query - User query
   * @param {string} pattern - Pattern to match
   * @returns {number} Similarity score (0-1)
   */
  calculateSimilarity(query, pattern) {
    const queryLower = query.toLowerCase();
    const patternLower = pattern.toLowerCase();
    
    // Remove parameter placeholders for similarity calculation
    const cleanPattern = patternLower.replace(/\{[^}]+\}/g, '').trim();
    
    // Simple word-based similarity
    const queryWords = queryLower.split(/\s+/);
    const patternWords = cleanPattern.split(/\s+/).filter(word => word.length > 0);
    
    if (patternWords.length === 0) return 0;
    
    let matches = 0;
    patternWords.forEach(patternWord => {
      if (queryWords.some(queryWord => 
        queryWord.includes(patternWord) || patternWord.includes(queryWord)
      )) {
        matches++;
      }
    });
    
    return matches / patternWords.length;
  }

  /**
   * Find the best matching query configuration
   * @param {string} query - User query
   * @returns {Object|null} Best matching query config
   */
  findBestMatch(query) {
    let bestMatch = null;
    let bestScore = 0;
    let bestParams = {};

    console.log('🔍 Finding best match for query:', query);

    // Search through all categories and queries
    Object.values(this.mappings.categories).forEach(category => {
      category.queries.forEach(queryConfig => {
        queryConfig.patterns.forEach(pattern => {
          const score = this.calculateSimilarity(query, pattern);

          if (score > bestScore && score > 0.3) { // Minimum threshold
            bestScore = score;
            bestMatch = queryConfig;
            bestParams = this.extractParameters(query, pattern);
          }
        });
      });
    });

    return bestMatch ? { ...bestMatch, extractedParams: bestParams, score: bestScore } : null;
  }

  /**
   * Route query to appropriate API endpoint
   * @param {string} query - User query
   * @returns {Promise<Object>} API response with metadata
   */
  async routeQuery(query) {
    console.log('🚀🚀🚀 ROUTE QUERY CALLED! 🚀🚀🚀');
    console.log('📝 Input query:', query);
    console.log('📝 Query type:', typeof query);
    console.log('📝 Query length:', query?.length);
    console.log('🔍 Routing query:', query);

    try {
      // Find best matching query configuration
      const matchedQuery = this.findBestMatch(query);

      if (!matchedQuery) {
        console.log('📋 No specific match found, using fallback');
        return this.executeFallback(query);
      }

      console.log('✅ Found match:', {
        id: matchedQuery.id,
        score: matchedQuery.score,
        params: matchedQuery.extractedParams,
        endpoint: matchedQuery.route.endpoint,
        method: matchedQuery.route.method
      });

      // Execute the matched query
      return this.executeQuery(matchedQuery, query);

    } catch (error) {
      return {
        success: false,
        error: error.message,
        query,
        route: null
      };
    }
  }

  /**
   * Execute a specific query configuration
   * @param {Object} queryConfig - Query configuration
   * @param {string} originalQuery - Original user query
   * @returns {Promise<Object>} API response
   */
  async executeQuery(queryConfig, originalQuery) {
    const { route, extractedParams, response_type } = queryConfig;

    try {
      let response;

      // Prepare endpoint URL with parameter substitution
      let endpoint = route.endpoint;
      Object.keys(extractedParams).forEach(key => {
        const placeholder = `{${key}}`;
        if (endpoint.includes(placeholder)) {
          endpoint = endpoint.replace(placeholder, encodeURIComponent(extractedParams[key]));
        }
      });

      // Prepare request parameters
      const requestParams = { ...route.params };
      Object.keys(requestParams).forEach(key => {
        if (requestParams[key] === 'extracted_from_query' && extractedParams[key]) {
          requestParams[key] = extractedParams[key];
        } else if (requestParams[key].startsWith('extracted_or_default_')) {
          const defaultValue = requestParams[key].replace('extracted_or_default_', '');
          requestParams[key] = extractedParams[key] || defaultValue;
        }
      });



      // Prepare request body
      let requestBody = route.body ? { ...route.body } : null;
      if (requestBody) {
        Object.keys(requestBody).forEach(key => {
          if (requestBody[key] === 'full_user_query') {
            requestBody[key] = originalQuery;
          } else if (requestBody[key] === 'extracted_cypher_query') {
            // Extract Cypher query from patterns like "execute cypher: MATCH..."
            const cypherMatch = originalQuery.match(/cypher:\s*(.+)/i);
            requestBody[key] = cypherMatch ? cypherMatch[1].trim() : originalQuery;
          } else if (typeof requestBody[key] === 'string' && requestBody[key].startsWith('extracted_')) {
            // Handle any extracted parameter: extracted_user_email, extracted_user_id, etc.
            const paramName = requestBody[key].replace('extracted_', '');
            if (extractedParams[paramName]) {
              requestBody[key] = extractedParams[paramName];
            }
          }
        });

        // Handle parameters object in request body
        if (requestBody.parameters && typeof requestBody.parameters === 'object') {
          Object.keys(requestBody.parameters).forEach(paramKey => {
            if (requestBody.parameters[paramKey] === 'extracted_from_query') {
              // Map user_name to name parameter for Cypher queries
              if (paramKey === 'name' && extractedParams.user_name) {
                console.log('🔍 PROFILE USER QUERY DEBUG:');
                console.log('📧 Extracted user_name:', extractedParams.user_name);
                console.log('📝 Original query:', originalQuery);
                console.log('🎯 Will search for name:', extractedParams.user_name);
                requestBody.parameters[paramKey] = extractedParams.user_name;
              } else if (extractedParams[paramKey]) {
                requestBody.parameters[paramKey] = extractedParams[paramKey];
              }
            }
          });
        }
      }

      // Execute API request
      const options = {
        params: requestParams,
        headers: route.headers ? {} : undefined
      };

      switch (route.method.toUpperCase()) {
        case 'GET':
          response = await this.apiService.get(endpoint, options);
          break;
        case 'POST':
          response = await this.apiService.post(endpoint, requestBody, options);
          break;
        case 'PUT':
          response = await this.apiService.put(endpoint, requestBody, options);
          break;
        case 'DELETE':
          response = await this.apiService.delete(endpoint, options);
          break;
        default:
          throw new Error(`Unsupported HTTP method: ${route.method}`);
      }
      
      return {
        ...response,
        query: originalQuery,
        route: {
          id: queryConfig.id,
          endpoint: endpoint,
          method: route.method,
          response_type
        },
        metadata: {
          matched_pattern: queryConfig.patterns[0],
          extracted_params: extractedParams,
          response_type
        }
      };
      
    } catch (error) {
      console.error('❌ Query execution error:', error);
      return {
        success: false,
        error: error.message,
        query: originalQuery,
        route: queryConfig.route
      };
    }
  }

  /**
   * Execute fallback query when no specific match is found
   * @param {string} query - User query
   * @returns {Promise<Object>} API response
   */
  async executeFallback(query) {
    const fallbackConfig = this.mappings.fallback;
    
    try {
      const response = await this.apiService.post(
        fallbackConfig.route.endpoint,
        { query },
        { headers: {} }
      );
      
      return {
        ...response,
        query,
        route: {
          id: 'fallback',
          endpoint: fallbackConfig.route.endpoint,
          method: fallbackConfig.route.method,
          response_type: fallbackConfig.response_type
        },
        metadata: {
          matched_pattern: 'fallback',
          extracted_params: {},
          response_type: fallbackConfig.response_type
        }
      };
      
    } catch (error) {
      console.error('❌ Fallback query error:', error);
      return {
        success: false,
        error: error.message,
        query,
        route: fallbackConfig.route
      };
    }
  }

  /**
   * Get sample queries for display
   * @returns {Array} Sample queries
   */
  getSampleQueries() {
    const samples = [];

    Object.values(this.mappings.categories).forEach(category => {
      category.queries.forEach(queryConfig => {
        // Respect hide_from_samples flag and ensure we have at least one pattern
        if (queryConfig.hide_from_samples) return;
        if (queryConfig.patterns && queryConfig.patterns.length > 0) {
          // Use sample_queries if available, otherwise use first pattern
          const sampleText = (queryConfig.sample_queries && queryConfig.sample_queries.length > 0)
            ? queryConfig.sample_queries[0]
            : queryConfig.patterns[0];

          samples.push({
            id: queryConfig.id,
            text: sampleText,
            description: queryConfig.description,
            category: category.name
          });
        }
      });
    });

    console.log('📋 QueryRouter generated', samples.length, 'sample queries');
    return samples;
  }
}

// Create singleton instance
const queryRouter = new QueryRouter();

export default queryRouter;
